import { useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useTours } from "@/lib/siteData";
import { useAdminSlots, useAdminBookings, useAdminResources, occupies, fmtDateTime, isUuid, numOrNull, todaySP } from "@/lib/booking";
import { Card, Field, Select, SaveBtn, Ok, Err, MultiCheck, SubTitle, useAction, smallBtn } from "./adminUI";

const WEEK = [["0", "Dom"], ["1", "Seg"], ["2", "Ter"], ["3", "Qua"], ["4", "Qui"], ["5", "Sex"], ["6", "Sáb"]];
const EMPTY = { tour_id: "", resource_id: "", from: "", to: "", weekdays: WEEK.map(([v]) => v), times: "", capacity: 2, price_override: "" };

function buildSlots(f) {
    const times = f.times.split(/[,\s]+/).filter((t) => /^\d{1,2}:\d{2}$/.test(t));
    const out = [];
    for (let d = new Date(`${f.from}T12:00:00Z`); d <= new Date(`${f.to || f.from}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) {
        if (!f.weekdays.includes(String(d.getUTCDay()))) continue;
        const day = d.toISOString().slice(0, 10);
        for (const t of times) out.push({ tour_id: f.tour_id, resource_id: f.resource_id || null, capacity: Number(f.capacity),
            price_override: numOrNull(f.price_override), starts_at: new Date(`${day}T${t.padStart(5, "0")}:00-03:00`).toISOString() });
    }
    return out;
}

export default function SlotsAdmin() {
    const tours = useTours(true).filter((t) => isUuid(t.id));
    const slots = useAdminSlots();
    const bookings = useAdminBookings();
    const resources = useAdminResources();
    const [f, setF] = useState(EMPTY);
    const { run, busy, msg, err } = useAction();
    const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));
    const names = Object.fromEntries(tours.map((t) => [t.id, t.title]));
    const resNames = Object.fromEntries((resources.data || []).map((r) => [r.id, r.name]));
    const occupied = useMemo(() => (bookings.data || []).filter(occupies).reduce((a, b) => ({ ...a, [b.slot_id]: (a[b.slot_id] || 0) + b.guests }), {}), [bookings.data]);
    const upcoming = (slots.data || []).filter((s) => new Date(s.starts_at) > new Date()).slice(0, 300);

    const create = () => run(async () => {
        if (!f.tour_id || !f.from) throw new Error("Selecione experiência e data inicial.");
        const tour=tours.find(t=>t.id===f.tour_id);
        const resource=(resources.data||[]).find(r=>r.id===f.resource_id);
        const isJetSki=["jetski_passeio","jetski_aluguel"].includes(tour?.category);
        if (isJetSki && !f.resource_id) throw new Error("Associe o jet ski cadastrado ao horário antes de disponibilizá-lo.");
        if (resource && resource.status!=="operational") throw new Error("Equipamento em manutenção ou fora de operação.");
        const capacity=Number(f.capacity);
        if (!Number.isInteger(capacity)||capacity<1||capacity>Number(tour?.capacity||0))
          throw new Error("Capacidade maior que o limite da experiência ou inválida.");
        if (resource && capacity>Number(resource.capacity)) throw new Error("Capacidade maior que a do equipamento.");
        const list = buildSlots(f);
        if (!list.length) throw new Error("Nenhum horário gerado. Confira dias da semana e horários (ex.: 09:00, 14:30).");
        if (list.length > 400) throw new Error("Máximo de 400 horários por vez.");
        return supabase.from("booking_slots").insert(list);
    }, "Horários cadastrados");

    return (
        <Card className="space-y-5">
            <SubTitle>Agenda — cadastrar horários</SubTitle>
            <div className="grid gap-4 sm:grid-cols-3" data-testid="slots-admin">
                <Select label="Experiência" value={f.tour_id} onChange={set("tour_id")} options={[["", "Selecione"], ...tours.map((t) => [t.id, t.title])]} testid="slot-tour" />
                <Select label="Equipamento (obrigatório para jet ski)" value={f.resource_id} onChange={set("resource_id")} options={[["", "Nenhum"], ...(resources.data || []).map((r) => [r.id, `${r.name} (${r.capacity})`])]} testid="slot-resource" />
                <Field label="Capacidade (vagas)" type="number" value={f.capacity} onChange={set("capacity")} testid="slot-capacity" />
                <Field label="De" type="date" value={f.from} onChange={set("from")} testid="slot-from" />
                <Field label="Até (opcional)" type="date" value={f.to} onChange={set("to")} testid="slot-to" />
                <Field label="Horários reais (ex.: 09:00, 14:30)" value={f.times} onChange={set("times")} testid="slot-times" />
                <Field label="Preço especial (opcional)" type="number" value={f.price_override} onChange={set("price_override")} />
            </div>
            <MultiCheck label="Dias da semana" options={WEEK} value={f.weekdays} onChange={set("weekdays")} />
            <div className="flex items-center justify-end gap-3"><Ok msg={msg} /><Err msg={err} /><SaveBtn onClick={create} saving={busy} testid="slot-create">Gerar horários</SaveBtn></div>
            <p className="text-xs text-dim">Conflitos de equipamento (incluindo duração e intervalo entre reservas) e manutenção são bloqueados pelo banco.</p>
            {upcoming.map((s) => (
                <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-ink/10 pt-3 text-sm" data-testid={`slot-row-${s.id}`}>
                    <span><strong>{names[s.tour_id] || "Experiência"}</strong> · {fmtDateTime(s.starts_at)} · {occupied[s.id] || 0}/{s.capacity} ocupadas
                        {s.resource_id ? ` · ${resNames[s.resource_id] || "equip."}` : ""} · {s.active ? "Ativo" : "Bloqueado"}</span>
                    <div className="flex gap-2">
                        <button className={smallBtn} disabled={busy} onClick={() => run(() => supabase.from("booking_slots").update({ active: !s.active }).eq("id", s.id))}>{s.active ? "Bloquear" : "Liberar"}</button>
                        <button className={smallBtn} disabled={busy || occupied[s.id] > 0} onClick={() => window.confirm("Excluir horário?") && run(() => supabase.from("booking_slots").delete().eq("id", s.id))}>Excluir</button>
                    </div>
                </div>
            ))}
            {!upcoming.length && <p className="text-xs text-dim">Nenhum horário futuro cadastrado (a partir de {todaySP()}).</p>}
        </Card>
    );
}
